import axios from 'axios';

// Get the token and add to requests
const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Use the same base URL pattern as your config.js
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
const NAV_IDS_API = `${API_BASE_URL}/nav_ids.php`;
const DROPDOWNS_API = `${API_BASE_URL}/dropdowns.php`;

// In-memory cache for fast lookups
let NAV_ID_CACHE = {};
let DROPDOWNS_CACHE = {
  products: [],
  grades: [],
  brands: [],
  pack_sizes: []
};
let isLoaded = false;

/**
 * Load dropdowns from database
 * Called once when the app starts or component mounts
 */
export const loadDropdowns = async () => {
  try {
    console.log('🔄 Loading dropdowns from database...');
    const types = ['products', 'grades', 'brands', 'pack_sizes'];
    
    for (const type of types) {
      try {
        const response = await axios.get(
          `${DROPDOWNS_API}?type=${type}`,
          { 
            withCredentials: true,
            headers: getAuthHeaders()
          }
        );
        
        if (response.data.success) {
          DROPDOWNS_CACHE[type] = response.data.data || [];
          console.log(`✅ ${type} loaded: ${DROPDOWNS_CACHE[type].length} items`);
        }
      } catch (err) {
        console.error(`Error loading ${type}:`, err);
      }
    }
    
    console.log('✅ All dropdowns loaded successfully');
    return true;
  } catch (error) {
    console.error('❌ Error loading dropdowns:', error);
    return false;
  }
};

/**
 * Get dropdown options cached in memory
 */
export const getDropdownOptions = () => {
  return DROPDOWNS_CACHE;
};

/**
 * Load NAV IDs from database into memory cache
 * This is called once when the app starts
 */
export const loadNavIdsFromDatabase = async () => {
  try {
    console.log('🔄 Loading NAV IDs from database...');
    const response = await axios.get(NAV_IDS_API, { 
      withCredentials: true,
      headers: getAuthHeaders()
    });
    
    if (response.data.success) {
      // Build nested object structure from flat database records
      NAV_ID_CACHE = {};
      let count = 0;
      
      response.data.data.forEach(mapping => {
        const product = mapping.product.toUpperCase().trim();
        const brand = mapping.brand.toUpperCase().trim();
        const grade = mapping.grade.toUpperCase().trim();
        const packSize = mapping.pack_size.toUpperCase().trim();
        
        if (!NAV_ID_CACHE[product]) {
          NAV_ID_CACHE[product] = {};
        }
        if (!NAV_ID_CACHE[product][brand]) {
          NAV_ID_CACHE[product][brand] = {};
        }
        if (!NAV_ID_CACHE[product][brand][grade]) {
          NAV_ID_CACHE[product][brand][grade] = {};
        }
        
        NAV_ID_CACHE[product][brand][grade][packSize] = mapping.nav_id;
        count++;
      });
      
      isLoaded = true;
      console.log(`✅ NAV IDs loaded from database: ${count} mappings`);
      console.log(`📦 Products loaded: ${Object.keys(NAV_ID_CACHE).length}`);
      return true;
    } else {
      throw new Error('Failed to load NAV IDs from database');
    }
  } catch (error) {
    console.error('❌ Error loading NAV IDs from database:', error);
    console.warn('⚠️ Falling back to hardcoded NAV ID mappings');
    
    // Fall back to hardcoded mappings if database fails
    NAV_ID_CACHE = getHardcodedMappings();
    isLoaded = true;
    return false;
  }
};

/**
 * Get NAV ID based on product, brand, grade, and pack size
 * @param {string} product - Product name
 * @param {string} brand - Brand name
 * @param {string} grade - Grade
 * @param {string} packSize - Pack size
 * @returns {string} NAV ID or 'NOT AVAILABLE'
 */
export const getNavId = (product, brand, grade, packSize) => {
  // Ensure NAV IDs are loaded
  if (!isLoaded) {
    console.warn('⚠️ NAV IDs not loaded yet, using hardcoded fallback');
    NAV_ID_CACHE = getHardcodedMappings();
    isLoaded = true;
  }

  // Normalize inputs
  const cleanProduct = (product || '').trim().toUpperCase();
  const cleanBrand = (brand || '').trim().toUpperCase();
  const cleanGrade = (grade || '').trim().toUpperCase();
  const cleanPackSize = (packSize || '').trim().toUpperCase();

  // Check if all required fields are provided
  if (!cleanProduct || !cleanBrand || !cleanGrade || !cleanPackSize) {
    return 'NOT AVAILABLE';
  }

  try {
    // Navigate the nested structure
    const navId = NAV_ID_CACHE?.[cleanProduct]?.[cleanBrand]?.[cleanGrade]?.[cleanPackSize];
    
    if (navId) {
      console.log(`✅ NAV ID found: ${navId} for ${cleanProduct} > ${cleanBrand} > ${cleanGrade} > ${cleanPackSize}`);
      return navId;
    } else {
      console.log(`⚠️ NAV ID not found for: ${cleanProduct} > ${cleanBrand} > ${cleanGrade} > ${cleanPackSize}`);
      return 'NOT AVAILABLE';
    }
  } catch (error) {
    console.error('❌ Error getting NAV ID:', error);
    return 'NOT AVAILABLE';
  }
};

/**
 * Add new NAV ID mapping to database
 * @param {string} product - Product name
 * @param {string} brand - Brand name
 * @param {string} grade - Grade
 * @param {string} packSize - Pack size
 * @param {string} navId - NAV ID
 * @returns {Promise<Object>} Result object with success status and message
 */
export const addNewNavMapping = async (product, brand, grade, packSize, navId) => {
  try {
    console.log('📝 Adding new NAV mapping:', { product, brand, grade, packSize, navId });
    
    const response = await axios.post(NAV_IDS_API, {
      product: product.trim().toUpperCase(),
      brand: brand.trim().toUpperCase(),
      grade: grade.trim().toUpperCase(),
      pack_size: packSize.trim().toUpperCase(),
      nav_id: navId.trim()
    }, { 
      withCredentials: true,
      headers: getAuthHeaders()
    });

    if (response.data.success) {
      // Update in-memory cache
      const cleanProduct = product.trim().toUpperCase();
      const cleanBrand = brand.trim().toUpperCase();
      const cleanGrade = grade.trim().toUpperCase();
      const cleanPackSize = packSize.trim().toUpperCase();
      
      if (!NAV_ID_CACHE[cleanProduct]) {
        NAV_ID_CACHE[cleanProduct] = {};
      }
      if (!NAV_ID_CACHE[cleanProduct][cleanBrand]) {
        NAV_ID_CACHE[cleanProduct][cleanBrand] = {};
      }
      if (!NAV_ID_CACHE[cleanProduct][cleanBrand][cleanGrade]) {
        NAV_ID_CACHE[cleanProduct][cleanBrand][cleanGrade] = {};
      }
      
      NAV_ID_CACHE[cleanProduct][cleanBrand][cleanGrade][cleanPackSize] = navId.trim();
      
      console.log(`✅ NAV ID ${response.data.action}:`, navId);
      return { 
        success: true, 
        message: response.data.message,
        action: response.data.action 
      };
    }
    
    return { success: false, message: 'Failed to add NAV ID' };
  } catch (error) {
    console.error('❌ Error adding NAV ID:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Failed to add NAV ID to database' 
    };
  }
};

/**
 * Get all available products from cache
 * @returns {Array<string>} List of product names
 */
export const getAvailableProducts = () => {
  return Object.keys(NAV_ID_CACHE).sort();
};

/**
 * Get available brands for a specific product
 * @param {string} product - Product name
 * @returns {Array<string>} List of brand names
 */
export const getAvailableBrands = (product) => {
  const cleanProduct = (product || '').trim().toUpperCase();
  const productMap = NAV_ID_CACHE[cleanProduct];
  return productMap ? Object.keys(productMap).sort() : [];
};

/**
 * Get available grades for a specific product and brand
 * @param {string} product - Product name
 * @param {string} brand - Brand name
 * @returns {Array<string>} List of grades
 */
export const getAvailableGrades = (product, brand) => {
  const cleanProduct = (product || '').trim().toUpperCase();
  const cleanBrand = (brand || '').trim().toUpperCase();
  const productMap = NAV_ID_CACHE[cleanProduct];
  if (!productMap) return [];
  const brandMap = productMap[cleanBrand];
  return brandMap ? Object.keys(brandMap).sort() : [];
};

/**
 * Get available pack sizes for a specific product, brand, and grade
 * @param {string} product - Product name
 * @param {string} brand - Brand name
 * @param {string} grade - Grade
 * @returns {Array<string>} List of pack sizes
 */
export const getAvailablePackSizes = (product, brand, grade) => {
  const cleanProduct = (product || '').trim().toUpperCase();
  const cleanBrand = (brand || '').trim().toUpperCase();
  const cleanGrade = (grade || '').trim().toUpperCase();
  const productMap = NAV_ID_CACHE[cleanProduct];
  if (!productMap) return [];
  const brandMap = productMap[cleanBrand];
  if (!brandMap) return [];
  const gradeMap = brandMap[cleanGrade];
  return gradeMap ? Object.keys(gradeMap).sort() : [];
};

/**
 * Export NAV IDs to JSON file (for backup)
 */
export const exportNavIdsToJson = () => {
  const jsonData = JSON.stringify(NAV_ID_CACHE, null, 2);
  const blob = new Blob([jsonData], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `NAV_IDS_BACKUP_${new Date().toISOString().split('T')[0]}.json`;
  link.click();
  URL.revokeObjectURL(url);
  console.log('✅ NAV IDs exported to JSON');
};

/**
 * Get cache statistics
 * @returns {Object} Statistics about the cache
 */
export const getCacheStats = () => {
  const products = Object.keys(NAV_ID_CACHE);
  let totalMappings = 0;
  
  products.forEach(product => {
    Object.keys(NAV_ID_CACHE[product]).forEach(brand => {
      Object.keys(NAV_ID_CACHE[product][brand]).forEach(grade => {
        totalMappings += Object.keys(NAV_ID_CACHE[product][brand][grade]).length;
      });
    });
  });
  
  return {
    isLoaded,
    totalProducts: products.length,
    totalMappings,
    products
  };
};

/**
/**
 * Hardcoded fallback mappings (from NAV_IDs.xlsx - 974 mappings)
 * This is used ONLY if database connection fails
 * Last updated: 2026-01-09
 */
const getHardcodedMappings = () => {
  console.log('⚠️ Using hardcoded NAV ID mappings as fallback');
  
  return {
      "CEZP": {
          "TC GOLD STAR": {
              "31/40": {
                  "5/4 LB": "BPFG-CEZP-001",
                  "4 LB": "BPSFG-CEZP-001"
              }
          },
          "GOLD STAR": {
              "31/40": {
                  "10/2 LB": "BPFG-CEZP-002",
                  "2 LB": "BPSFG-CEZP-002"
              }
          },
          "FINAL BAG BULK": {
              "31/40": {
                  "4/4 LB": "BPSFG-CEZP-FBBC-001",
                  "5/4 LB": "BPSFG-CEZP-FBBC-002",
                  "10/2 LB": "BPSFG-CEZP-FBBC-003",
                  "4 LB": "BPSFG-CEZP-FBBG-001",
                  "2 LB": "BPSFG-CEZP-FBBG-003"
              }
          },
          "PLAIN BAG BULK": {
              "31/40": {
                  "1/9 KG": "BPSFG-CEZP-PBBC-001",
                  "1/4 KG": "BPSFG-CEZP-PBBC-002",
                  "10/2 LB": "BPSFG-CEZP-PBBC-004",
                  "1/10 KG": "BPSFG-CEZP-PBBC-005",
                  "9 KG": "BPSFG-CEZP-PBBG-001",
                  "4 KG": "BPSFG-CEZP-PBBG-002",
                  "2 LB": "BPSFG-CEZP-PBBG-004",
                  "10 KG": "BPSFG-CEZP-PBBG-005",
                  "12 OZ": "BPSFG-CEZP-PBBG-006"
              }
          },
          "SISTER SIZE": {
              "31/40": {
                  "1/10 KG": "BPSFG-CEZP-PBBC-003",
                  "10 KG": "BPSFG-CEZP-PBBG-003"
              }
          }
      },
      "CEZPNC": {
          "NATURES PROMISE": {
              "31/40": {
                  "12/12 OZ": "BPFG-CEZPNC-001",
                  "12 OZ": "BPSFG-CEZPNC-001"
              }
          },
          "SISTER SIZE": {
              "31/40": {
                  "1/10 KG": "BPSFG-CEZPNCPBBG-001"
              }
          },
          "PLAIN BAG BULK": {
              "31/40": {
                  "1/10 KG": "BPSFG-CEZPNCPBBC-002",
                  "10 KG": "BPSFG-CEZPNCPBBG-002"
              }
          },
          "REJECTION": {
              "31/40": {
                  "1/10 KG": "BPSFG-CEZPNCPBBC-003",
                  "10 KG": "BPSFG-CEZPNCPBBG-003"
              }
          }
      },
      "CIST EZP CUT": {
          "WHOLESOME PANTRY": {
              "31/40": {
                  "5/2 LB": "BPFG-CIST EZP CU-001",
                  "20/1 LB": "BPFG-CIST EZP CU-013",
                  "2 LB": "BPSFG-CIST EZP C-001",
                  "1 LB": "BPSFG-CIST EZP C-013"
              },
              "21/25": {
                  "5/2 LB": "BPFG-CIST EZP CU-002",
                  "20/1 LB": "BPFG-CIST EZP CU-012",
                  "2 LB": "BPSFG-CIST EZP C-002",
                  "1 LB": "BPSFG-CIST EZP C-012"
              },
              "16/20": {
                  "5/2 LB": "BPFG-CIST EZP CU-003",
                  "20/1 LB": "BPFG-CIST EZP CU-011",
                  "2 LB": "BPSFG-CIST EZP C-003",
                  "1 LB": "BPSFG-CIST EZP C-011"
              }
          },
          "NATURES PROMISE": {
              "41/50": {
                  "12/12 OZ": "BPFG-CIST EZP CU-004",
                  "12 OZ": "BPSFG-CIST EZP C-004"
              },
              "21/25": {
                  "12/12 OZ": "BPFG-CIST EZP CU-006",
                  "12 OZ": "BPSFG-CIST EZP C-006"
              }
          },
          "TC HEB": {
              "31/40": {
                  "3/7 LB": "BPFG-CIST EZP CU-005",
                  "7 LB": "BPSFG-CIST EZP C-005"
              }
          },
          "TC HARRIS TEETER": {
              "26/30": {
                  "4/5 LB": "BPFG-CIST EZP CU-009",
                  "5 LB": "BPSFG-CIST EZP C-009"
              },
              "31/40": {
                  "4/5 LB": "BPFG-CIST EZP CU-010",
                  "5 LB": "BPSFG-CIST EZP C-010"
              }
          },
          "HARRIS TEETER  FM": {
              "16/20": {
                  "5/2 LB": "BPFG-CIST EZP CU-014",
                  "2 LB": "BPSFG-CIST EZP C-014"
              },
              "26/30": {
                  "5/2 LB": "BPFG-CIST EZP CU-015",
                  "2 LB": "BPSFG-CIST EZP C-015"
              }
          },
          "BLACK TAIL": {
              "31/40": {
                  "10 KG": "BPSFG-CIST EZP C-007",
                  "1/10 KG": "BPSFG-CIST EZP C-008"
              },
              "16/20": {
                  "1/10 KG": "BPSFG-CIST-PBBC-016",
                  "10 KG": "BPSFG-CIST-PBBG-016"
              }
          },
          "FINAL BAG BULK": {
              "31/40": {
                  "3/7 LB": "BPSFG-CISTE-FBBC-001",
                  "15/1 LB": "BPSFG-CISTE-FBBC-009",
                  "7 LB": "BPSFG-CISTE-FBBG-002",
                  "1 LB": "BPSFG-CISTE-FBBG-009"
              },
              "26/30": {
                  "4/5 LB": "BPSFG-CISTE-FBBC-003",
                  "6/5 LB": "BPSFG-CISTE-FBBC-004",
                  "4/4 LB": "BPSFG-CISTE-FBBC-005",
                  "10/2 LB": "BPSFG-CISTE-FBBC-008",
                  "5 LB": "BPSFG-CISTE-FBBG-004",
                  "4 LB": "BPSFG-CISTE-FBBG-005",
                  "2 LB": "BPSFG-CISTE-FBBG-008"
              },
              "21/25": {
                  "15/1 LB": "BPSFG-CISTE-FBBC-006",
                  "14/1 LB": "BPSFG-CISTE-FBBC-06A",
                  "1 LB": "BPSFG-CISTE-FBBG-006"
              },
              "16/20": {
                  "15/1 LB": "BPSFG-CISTE-FBBC-007",
                  "8/2 LB": "BPSFG-CISTE-FBBC-010",
                  "1 LB": "BPSFG-CISTE-FBBG-007",
                  "2 LB": "BPSFG-CISTE-FBBG-010"
              }
          },
          "PLAIN BAG BULK": {
              "31/40": {
                  "1/9 KG": "BPSFG-CISTE-PBBC-001",
                  "9 KG": "BPSFG-CISTE-PBBG-001",
                  "10 KG": "BPSFG-CISTEZPCPB-004",
                  "1/10 KG": "BPSFG-CISTEZPCPC-004"
              },
              "16/20": {
                  "1/10 KG": "BPSFG-CISTE-PBBC-002",
                  "10 KG": "BPSFG-CISTE-PBBG-002",
                  "2 LB": "BPSFG-CISTEZPCPB-010",
                  "5/2 LB": "BPSFG-CISTEZPCPC-010"
              },
              "21/25": {
                  "1/9 KG": "BPSFG-CISTE-PBBC-003",
                  "9 KG": "BPSFG-CISTE-PBBG-003",
                  "1/10 KG": "BPSFG-CISTEZPCPB-007",
                  "10 KG": "BPSFG-CISTEZPCPC-007"
              },
              "26/30": {
                  "10 KG": "BPSFG-CISTEZPCPB-006",
                  "1/10 KG": "BPSFG-CISTEZPCPC-006"
              },
              "41/50": {
                  "1/10 KG": "BPSFG-CIST-PBBC-010",
                  "10 KG": "BPSFG-CIST-PBBG-010"
              }
          },
          "SISTER SIZE": {
              "16/20": {
                  "1/10 KG": "BPSFG-CISTE-PBBC-02A",
                  "10 KG": "BPSFG-CISTE-PBBG-02A"
              },
              "41/50": {
                  "10 KG": "BPSFG-CISTEZPCPB-001",
                  "1/10 KG": "BPSFG-CISTEZPCPC-001"
              },
              "21/25": {
                  "10 KG": "BPSFG-CISTEZPCPB-002",
                  "1/10 KG": "BPSFG-CISTEZPCPC-002"
              },
              "31/40": {
                  "10 KG": "BPSFG-CISTEZPCPB-003",
                  "9 KG": "BPSFG-CISTEZPCPB-008",
                  "1/10 KG": "BPSFG-CISTEZPCPC-003",
                  "1/9 KG": "BPSFG-CISTEZPCPC-008"
              },
              "26/30": {
                  "10 KG": "BPSFG-CISTEZPCPB-005",
                  "9 KG": "BPSFG-CISTEZPCPB-009",
                  "1/10 KG": "BPSFG-CISTEZPCPC-005",
                  "1/9 KG": "BPSFG-CISTEZPCPC-009"
              }
          }
      },
      "CIST": {
          "FREMONT FISH MARKET": {
              "26/30": {
                  "16/12 OZ": "BPFG-CIST-001",
                  "12 OZ": "BPSFG-CIST-001"
              },
              "31/40": {
                  "16/12 OZ": "BPFG-CIST-002",
                  "12 OZ": "BPSFG-CIST-002"
              }
          },
          "FINAL BAG BULK": {
              "21/25": {
                  "10/2 LB": "BPSFG-CIST-FBBC-001",
                  "5/2 LB": "BPSFG-CIST-FBBC-003",
                  "8/2 LB": "BPSFG-CIST-FBBC-004",
                  "2 LB": "BPSFG-CIST-FBBG-001"
              },
              "31/40": {
                  "24/12 OZ": "BPSFG-CIST-FBBC-005",
                  "20/12 OZ": "BPSFG-CIST-FBBC-005A",
                  "12 OZ": "BPSFG-CIST-FBBG-005"
              }
          },
          "PLAIN BAG BULK": {
              "21/25": {
                  "5/2 LB": "BPSFG-CIST-PBBC-002",
                  "10/2 LB": "BPSFG-CIST-PBBC-004",
                  "2 LB": "BPSFG-CIST-PBBG-002"
              },
              "26/30": {
                  "1/9 KG": "BPSFG-CIST-PBBC-005",
                  "1/10 KG": "BPSFG-CIST-PBBC-011A",
                  "9 KG": "BPSFG-CIST-PBBG-005",
                  "1 KG": "BPSFG-CIST-PBBG-006",
                  "10 KG": "BPSFG-CIST-PBBG-011A"
              },
              "41/60": {
                  "25/12 OZ": "BPSFG-CIST-PBBC-007",
                  "12 OZ": "BPSFG-CIST-PBBG-007"
              },
              "31/40": {
                  "1/10 KG": "BPSFG-CIST-PBBC-013",
                  "10 KG": "BPSFG-CIST-PBBG-013"
              },
              "51/60": {
                  "1/10 KG": "BPSFG-CIST-PBBC-014",
                  "10 KG": "BPSFG-CIST-PBBG-014"
              },
              "ALL MIX": {
                  "1/10 KG": "BPSFG-CIST-PBBC-015",
                  "10 KG": "BPSFG-CIST-PBBG-015"
              }
          },
          "BLACK TAIL": {
              "26/30": {
                  "1 KG": "BPSFG-CIST-PBBC-006",
                  "1/10 KG": "BPSFG-CIST-PBBC-012",
                  "10 KG": "BPSFG-CIST-PBBG-012"
              }
          },
          "REJECTION": {
              "ALL MIX": {
                  "1/1 KG": "BPSFG-CIST-PBBC-008",
                  "1/10 KG": "BPSFG-CIST-PBBC-008A",
                  "1 KG": "BPSFG-CIST-PBBG-008",
                  "10 KG": "BPSFG-CIST-PBBG-008A"
              },
              "21/25": {
                  "1/10 KG": "BPSFG-CIST-PBBC-009",
                  "10 KG": "BPSFG-CIST-PBBG-009"
              }
          }
      },
      "CISTSR": {
          "TC HEB": {
              "31/40": {
                  "12/16 OZ": "BPFG-CISTSR-001",
                  "16 OZ": "BPSFG-CISTSRTRAY-001"
              }
          },
          "FREMONT FISH MARKET": {
              "51/60": {
                  "10/16 OZ": "BPFG-CISTSR-002",
                  "16 OZ": "BPSFG-CISTSR-TRA-002"
              }
          },
          "PLAIN BAG BULK": {
              "51/60": {
                  "12/12 OZ": "BPFG-CISTSR-PBBC-001"
              }
          },
          "TRAY BULK": {
              "51/60": {
                  "12 OZ": "BPFG-CISTSR-PBBT-001"
              }
          }
      },
      "CISTSR-CM": {
          "BOWL AND BASKET": {
              "51/60": {
                  "12/16 OZ": "BPFG-CISTSR-003",
                  "16 OZ": "BPSFG-CISTSR-003"
              }
          },
          "PLAIN BAG BULK": {
              "51/60": {
                  "14/12 OZ": "BPSFG-CISTSR-PBBC-03",
                  "12/12 OZ": "BPSFG-CISTSR-PBBC-3A",
                  "12 OZ": "BPSFG-CISTSR-PBBG-03",
                  "1/10 KG": "BPSG-CISTSR-PBBC-002",
                  "10 KG": "BPSG-CISTSR-PBBG-002"
              }
          }
      },
      "CIST EZP CUT-TG": {
          "TC HEB": {
              "16/20": {
                  "5/2 LB": "BPFG-CISTTG-001",
                  "2 LB": "BPSFG-CISTTG-001"
              },
              "21/25": {
                  "5/2 LB": "BPFG-CISTTG-006",
                  "2 LB": "BPSFG-CISTTG-006"
              },
              "13/15": {
                  "5/2 LB": "BPFG-CISTTG-007",
                  "2 LB": "BPSFG-CISTTG-007"
              }
          },
          "SPECIALLY SELECTED": {
              "16/20": {
                  "10/1 LB": "BPFG-CISTTG-002",
                  "1 LB": "BPSFG-CISTTG-002"
              }
          },
          "BOWL AND BASKET": {
              "13/15": {
                  "5/2 LB": "BPFG-CISTTG-003",
                  "2 LB": "BPSFG-CISTTG-003"
              }
          },
          "HARRIS TEETER  FM": {
              "13/15": {
                  "5/2 LB": "BPFG-CISTTG-005",
                  "2 LB": "BPSFG-CISTTG-005"
              }
          },
          "PLAIN BAG BULK": {
              "13/15": {
                  "1/10 KG": "BPSFG-CISTTG-PBBC-05",
                  "10 KG": "BPSFG-CISTTG-PBBG-05"
              },
              "16/20": {
                  "1/10 KG": "BPFG-CISTTG-PBBC-003",
                  "10/2 LB": "BPFG-CISTTG-PBBC-004",
                  "8/2 LB": "BPFG-CISTTG-PBBC-04A",
                  "10 KG": "BPFG-CISTTG-PBBG-03A",
                  "2 LB": "BPFG-CISTTG-PBBG-004"
              },
              "26/30": {
                  "1/10 KG": "BPFG-CISTTG-PBBC-005",
                  "10 KG": "BPFG-CISTTG-PBBG-005"
              },
              "21/25": {
                  "1/10 KG": "BPSFG-CISTTG-PBBC-02",
                  "8/2 LB": "BPSFG-CISTTG-PBBC-06",
                  "10 KG": "BPSFG-CISTTG-PBBG-02",
                  "2 LB": "BPSFG-CISTTG-PBBG-06"
              }
          },
          "SISTER SIZE": {
              "16/20": {
                  "1/10 KG": "BPFG-CISTTG-PBBC-03A"
              },
              "21/25": {
                  "1/10 KG": "BPSFG-CISTTG-PBBC-2A",
                  "10 KG": "BPSFG-CISTTG-PBBG-2A"
              }
          },
          "FINAL BAG BULK": {
              "13/15": {
                  "10/2 LB": "BPSFG-CISTTG-FBBC-1A",
                  "2 LB": "BPSFG-CISTTG-FBBG-1A"
              },
              "16/20": {
                  "5/2 LB": "BPSFG-CISTTG-FBBC-02",
                  "20/1 LB": "BPSFG-CISTTG-FBBC-03",
                  "9/2 LB": "BPSFG-CISTTG-FBBC-2A",
                  "15/1 LB": "BPSFG-CISTTG-FBBC-3A",
                  "2 LB": "BPSFG-CISTTG-FBBG-02",
                  "1 LB": "BPSFG-CISTTG-FBBG-03"
              },
              "21/25": {
                  "8/2 LB": "BPSFG-CISTTG-FBBC-6A",
                  "2 LB": "BPSFG-CISTTG-FBBG-6A"
              }
          }
      },
      "CPND": {
          "GREAT CATCH": {
              "100/150": {
                  "12/12 OZ": "BPFG-CPND-001",
                  "12 OZ": "BPSFG-CPND-001"
              },
              "71/90": {
                  "10/16 OZ": "BPFG-CPND-002",
                  "16 OZ": "BPSFG-CPND-002"
              }
          },
          "FINAL BAG BULK": {
              "100/150": {
                  "24/12 OZ": "BPSFG-CPND-FBBC-001",
                  "12 OZ": "BPSFG-CPND-FBBG-001"
              }
          },
          "PLAIN BAG BULK": {
              "100/150": {
                  "20/12 OZ": "BPSFG-CPND-PBBC-001",
                  "1/12 KG": "BPSFG-CPND-PBBC-002",
                  "1/9 KG": "BPSFG-CPND-PBBC-004",
                  "1/10 KG": "BPSFG-CPND-PBBC-005",
                  "25/12 OZ": "BPSFG-CPND-PBBC-010",
                  "12 OZ": "BPSFG-CPND-PBBG-010",
                  "12 KG": "BPSFG-CPND-PBBG-002",
                  "9 KG": "BPSFG-CPND-PBBG-004",
                  "10 KG": "BPSFG-CPND-PBBG-005"
              },
              "100/200": {
                  "1/5 KG": "BPSFG-CPND-PBBC-003",
                  "5 KG": "BPSFG-CPND-PBBG-003"
              },
              "91/110": {
                  "1/10 KG": "BPSFG-CPND-PBBC-006",
                  "10 KG": "BPSFG-CPND-PBBG-006"
              },
              "71/90": {
                  "1/4 KG": "BPSFG-CPND-PBBC-011",
                  "1/10 KG": "BPSFG-CPND-PBBC-012",
                  "4 KG": "BPSFG-CPND-PBBG-011",
                  "10 KG": "BPSFG-CPND-PBBG-012"
              },
              "60/80": {
                  "1/10 KG": "BPSFG-CPND-PBBC-016",
                  "10 KG": "BPSFG-CPND-PBBG-016"
              }
          },
          "SISTER SIZE": {
              "71/90": {
                  "1/10 KG": "BPSFG-CPND-PBBC-007",
                  "10 KG": "BPSFG-CPND-PBBG-007"
              },
              "100/150": {
                  "1/10 KG": "BPSFG-CPND-PBBC-008",
                  "10 KG": "BPSFG-CPND-PBBG-008"
              },
              "91/110": {
                  "1/10 KG": "BPSFG-CPND-PBBC-009",
                  "10 KG": "BPSFG-CPND-PBBG-009"
              }
          }
      },
      "CPND-NP": {
          "HARRIS TEETER  FM": {
              "41/50": {
                  "12/12 OZ": "BPFG-CPNDNP-001",
                  "12 OZ": "BPSFG-CPNDNP-001"
              },
              "71/90": {
                  "12/12 OZ": "BPFG-CPNDNP-002",
                  "12 OZ": "BPSFG-CPNDNP-002"
              }
          },
          "PLAIN BAG BULK": {
              "41/50": {
                  "1/10 KG": "BPSFG-CPNDNP-PBBC-01",
                  "10 KG": "BPSFG-CPNDNP-PBBG-01"
              },
              "71/90": {
                  "1/10 KG": "BPSFG-CPNDNP-PBBC-02",
                  "10 KG": "BPSFG-CPNDNP-PBBG-02"
              }
          }
      },
      "CPTO": {
          "GOLD STAR": {
              "31/40": {
                  "10/1 LB": "BPFG-CPTO-001",
                  "5/2 LB": "BPFG-CPTO-007",
                  "1 LB": "BPSFG-CPTO-BG-001",
                  "2 LB": "BPSFG-CPTO-BG-007"
              },
              "26/30": {
                  "10/1 LB": "BPFG-CPTO-002",
                  "5/2 LB": "BPFG-CPTO-010",
                  "2 LB": "BPSFG-CPTO-010",
                  "1 LB": "BPSFG-CPTO-BG-002"
              },
              "51/60": {
                  "5/2 LB": "BPFG-CPTO-016",
                  "2 LB": "BPSFG-CPTO-016"
              }
          },
          "BOWL AND BASKET": {
              "31/40": {
                  "5/2 LB": "BPFG-CPTO-003",
                  "20/1 LB": "BPFG-CPTO-022",
                  "1 LB": "BPSFG-CPTO-022",
                  "2 LB": "BPSFG-CPTO-BG-003"
              },
              "21/25": {
                  "5/2 LB": "BPFG-CPTO-005",
                  "2 LB": "BPSFG-CPTO-005"
              },
              "51/60": {
                  "5/2 LB": "BPFG-CPTO-020",
                  "2 LB": "BPSFG-CPTO-020"
              }
          },
          "GREAT CATCH": {
              "26/30": {
                  "10/16 OZ": "BPFG-CPTO-004",
                  "10/2 LB": "BPFG-CPTO-009",
                  "16 OZ": "BPSFG-CPTO-BG-004",
                  "2 LB": "BPSFG-CPTO-BG-009"
              },
              "41/60": {
                  "12/12 OZ": "BPFG-CPTO-006",
                  "12 OZ": "BPSFG-CPTO-006"
              },
              "41/50": {
                  "10/16 OZ": "BPFG-CPTO-008",
                  "16 OZ": "BPSFG-CPTO-008"
              }
          },
          "SOUTHERN SEAS": {
              "41/50": {
                  "10/1 LB": "BPFG-CPTO-011",
                  "1 LB": "BPSFG-CPTO-011"
              },
              "26/30": {
                  "10/1 LB": "BPFG-CPTO-015",
                  "1 LB": "BPSFG-CPTO-015"
              },
              "16/20": {
                  "5/2 LB": "BPFG-CPTO-017",
                  "2 LB": "BPSFG-CPTO-017"
              },
              "31/40": {
                  "5/2 LB": "BPFG-CPTO-018",
                  "10/1 LB": "BPFG-CPTO-019",
                  "2 LB": "BPSFG-CPTO-018",
                  "1 LB": "BPSFG-CPTO-019"
              }
          },
          "MARKET BASKET": {
              "26/30": {
                  "10/2 LB": "BPFG-CPTO-012",
                  "20/1 LB": "BPFG-CPTO-013",
                  "2 LB": "BPSFG-CPTO-012",
                  "1 LB": "BPSFG-CPTO-013"
              },
              "41/50": {
                  "10/2 LB": "BPFG-CPTO-014",
                  "2 LB": "BPSFG-CPTO-014"
              }
          },
          "SEAQUEEN": {
              "71/90": {
                  "16/12 OZ": "BPFG-CPTO-021",
                  "12 OZ": "BPSFG-CPTO-021"
              }
          },
          "SAMPLES": {
              "16/20": {
                  "4 LB": "BPSFG-CPTO-BG-010"
              },
              "26/30": {
                  "4 LB": "BPSFG-CPTO-BG-011"
              },
              "31/40": {
                  "4 LB": "BPSFG-CPTO-BG-012"
              }
          },
          "FINAL BAG BULK": {
              "31/40": {
                  "15/1 LB": "BPSFG-CPTO-FBBC-001",
                  "20/1 LB": "BPSFG-CPTO-FBBC-002A",
                  "8/1 LB": "BPSFG-CPTO-FBBC-003",
                  "16/1 LB": "BPSFG-CPTO-FBBC-004A",
                  "12/1 LB": "BPSFG-CPTO-FBBC-004B",
                  "25/1 LB": "BPSFG-CPTO-FBBC-005",
                  "5/2 LB": "BPSFG-CPTO-FBBC-008A",
                  "10/2 LB": "BPSFG-CPTO-FBBC-015",
                  "15/2 LB": "BPSFG-CPTO-FBBC-014",
                  "9/2 LB": "BPSFG-CPTO-FBBC-015A",
                  "1 LB": "BPSFG-CPTO-FBBG-004A",
                  "2 LB": "BPSFG-CPTO-FBBG-015"
              },
              "41/60": {
                  "20/16 OZ": "BPSFG-CPTO-FBBC-006",
                  "24/12 OZ": "BPSFG-CPTO-FBBC-012",
                  "20/12 OZ": "BPSFG-CPTO-FBBC-012A",
                  "16 OZ": "BPSFG-CPTO-FBBG-006",
                  "12 OZ": "BPSFG-CPTO-FBBG-012"
              },
              "21/25": {
                  "10/2 LB": "BPSFG-CPTO-FBBC-007",
                  "2 LB": "BPSFG-CPTO-FBBG-007"
              },
              "26/30": {
                  "18/16 OZ": "BPSFG-CPTO-FBBC-010",
                  "20/16 OZ": "BPSFG-CPTO-FBBC-011",
                  "10/2 LB": "BPSFG-CPTO-FBBC-013",
                  "20/1 LB": "BPSFG-CPTO-FBBC-018",
                  "15/1 LB": "BPSFG-CPTO-FBBC-18A",
                  "16 OZ": "BPSFG-CPTO-FBBG-010",
                  "2 LB": "BPSFG-CPTO-FBBG-013",
                  "1 LB": "BPSFG-CPTO-FBBG-018"
              },
              "51/60": {
                  "10/2 LB": "BPSFG-CPTO-FBBC-016",
                  "5/2 LB": "BPSFG-CPTO-FBBC-016A"
              },
              "71/90": {
                  "24/12 OZ": "BPSFG-CPTO-FBBC-017",
                  "12 OZ": "BPSFG-CPTO-FBBG-017"
              },
              "41/50": {
                  "20/1 LB": "BPSFG-CPTO-FBBC-019",
                  "1 LB": "BPSFG-CPTO-FBBG-019"
              }
          },
          "PLAIN BAG BULK": {
              "26/30": {
                  "20/1 LB": "BPSFG-CPTO-PBBC-001",
                  "15/1 LB": "BPSFG-CPTO-PBBC-001A",
                  "1/9 KG": "BPSFG-CPTO-PBBC-023",
                  "1/10 KG": "BPSFG-CPTO-PBBC-037",
                  "1 LB": "BPSFG-CPTO-PBBG-001",
                  "9 KG": "BPSFG-CPTO-PBBG-023",
                  "10 KG": "BPSFG-CPTO-PBBG-037"
              },
              "31/40": {
                  "12/1 LB": "BPSFG-CPTO-PBBC-002",
                  "1/12.7 KG": "BPSFG-CPTO-PBBC-003",
                  "1/28.8 KG": "BPSFG-CPTO-PBBC-004",
                  "1/1.4 KG": "BPSFG-CPTO-PBBC-006",
                  "1/16 KG": "BPSFG-CPTO-PBBC-007",
                  "1/12 KG": "BPSFG-CPTO-PBBC-010",
                  "1/3 KG": "BPSFG-CPTO-PBBC-011",
                  "10/2 LB": "BPSFG-CPTO-PBBC-016",
                  "8/2 LB": "BPSFG-CPTO-PBBC-017",
                  "5/2 LB": "BPSFG-CPTO-PBBC-018",
                  "1/9 KG": "BPSFG-CPTO-PBBC-024",
                  "1/10 KG": "BPSFG-CPTO-PBBC-041",
                  "1/15 KG": "BPSFG-CPTO-PBBC-042",
                  "10/1 LB": "BPSFG-CPTO-PBBC-045",
                  "1 LB": "BPSFG-CPTO-PBBG-045",
                  "12.7 KG": "BPSFG-CPTO-PBBG-003",
                  "28.8 KG": "BPSFG-CPTO-PBBG-004",
                  "200 GRMS": "BPSFG-CPTO-PBBG-005",
                  "1.4 KG": "BPSFG-CPTO-PBBG-006",
                  "16 KG": "BPSFG-CPTO-PBBG-007",
                  "12 KG": "BPSFG-CPTO-PBBG-010",
                  "3 KG": "BPSFG-CPTO-PBBG-011",
                  "2 LB": "BPSFG-CPTO-PBBG-016",
                  "9 KG": "BPSFG-CPTO-PBBG-024",
                  "10 KG": "BPSFG-CPTO-PBBG-041",
                  "15 KG": "BPSFG-CPTO-PBBG-042",
                  "16 OZ": "BPSFG-CPTO-PBBG-048"
              },
              "41/60": {
                  "25/1 LB": "BPSFG-CPTO-PBBC-008",
                  "1/1 KG": "BPSFG-CPTO-PBBC-009",
                  "10/1 LB": "BPSFG-CPTO-PBBC-014",
                  "25/340 G": "BPSFG-CPTO-PBBC-015",
                  "12/12 OZ": "BPSFG-CPTO-PBBC-019",
                  "10/12 OZ": "BPSFG-CPTO-PBBC-020",
                  "20/12 OZ": "BPSFG-CPTO-PBBC-021",
                  "35/12 OZ": "BPSFG-CPTO-PBBC-022",
                  "1/9 KG": "BPSFG-CPTO-PBBC-025",
                  "1/5 KG": "BPSFG-CPTO-PBBC-027",
                  "1/10 KG": "BPSFG-CPTO-PBBC-030",
                  "1 LB": "BPSFG-CPTO-PBBG-008",
                  "1 KG": "BPSFG-CPTO-PBBG-009",
                  "340 G": "BPSFG-CPTO-PBBG-015",
                  "12 OZ": "BPSFG-CPTO-PBBG-019",
                  "9 KG": "BPSFG-CPTO-PBBG-025",
                  "5 KG": "BPSFG-CPTO-PBBG-027",
                  "10 KG": "BPSFG-CPTO-PBBG-030"
              },
              "41/50": {
                  "30/1 LB": "BPSFG-CPTO-PBBC-012",
                  "27/1 LB": "BPSFG-CPTO-PBBC-013",
                  "1/10 KG": "BPSFG-CPTO-PBBC-031",
                  "1 LB": "BPSFG-CPTO-PBBG-012",
                  "10 KG": "BPSFG-CPTO-PBBG-031",
                  "12 OZ": "BPSFG-CPTO-PBBG-049"
              },
              "21/25": {
                  "1/9 KG": "BPSFG-CPTO-PBBC-026",
                  "10/2 LB": "BPSFG-CPTO-PBBC-034",
                  "1/10 KG": "BPSFG-CPTO-PBBC-039",
                  "9 KG": "BPSFG-CPTO-PBBG-026",
                  "2 LB": "BPSFG-CPTO-PBBG-034",
                  "10 KG": "BPSFG-CPTO-PBBG-039",
                  "12 OZ": "BPSFG-CPTO-PBBG-050"
              },
              "16/20": {
                  "10/2 LB": "BPSFG-CPTO-PBBC-029",
                  "1/10 KG": "BPSFG-CPTO-PBBC-047",
                  "2 LB": "BPSFG-CPTO-PBBG-029",
                  "10 KG": "BPSFG-CPTO-PBBG-047"
              },
              "51/60": {
                  "1/10 KG": "BPSFG-CPTO-PBBC-043",
                  "10 KG": "BPSFG-CPTO-PBBG-043"
              },
              "71/90": {
                  "1/10 KG": "BPSFG-CPTO-PBBC-044",
                  "24/12 OZ": "BPSFG-CPTO-PBBC-046",
                  "10 KG": "BPSFG-CPTO-PBBG-044",
                  "12 OZ": "BPSFG-CPTO-PBBG-046"
              }
          },
          "SISTER SIZE": {
              "41/60": {
                  "1/10 KG": "BPSFG-CPTO-PBBC-028",
                  "10 KG": "BPSFG-CPTO-PBBG-028"
              },
              "41/50": {
                  "1/10 KG": "BPSFG-CPTO-PBBC-031S",
                  "10 KG": "BPSFG-CPTO-PBBG-031S"
              },
              "21/25": {
                  "1/10 KG": "BPSFG-CPTO-PBBC-032",
                  "10 KG": "BPSFG-CPTO-PBBG-032"
              },
              "31/40": {
                  "1/10 KG": "BPSFG-CPTO-PBBC-033",
                  "10 KG": "BPSFG-CPTO-PBBG-033"
              },
              "26/30": {
                  "1/10 KG": "BPSFG-CPTO-PBBC-035",
                  "10 KG": "BPSFG-CPTO-PBBG-035"
              },
              "71/90": {
                  "1/10 KG": "BPSFG-CPTO-PBBC-044S",
                  "10 KG": "BPSFG-CPTO-PBBG-044S"
              }
          },
          "REJECTION": {
              "61/70": {
                  "1/10 KG": "BPSFG-CPTO-PBBC-036",
                  "10 KG": "BPSFG-CPTO-PBBG-036"
              },
              "41/60": {
                  "1/10 KG": "BPSFG-CPTO-PBBC-038",
                  "10 KG": "BPSFG-CPTO-PBBG-038"
              },
              "41/50": {
                  "1/10 KG": "BPSFG-CPTO-PBBC-040",
                  "10 KG": "BPSFG-CPTO-PBBG-040"
              }
          }
      },
      "CPTO-NP": {
          "PLAIN-HARRIS TEETER": {
              "31/40": {
                  "20/1 LB": "BPFG-CPTONP-001",
                  "1 LB": "BPSFG-CPTONP-001"
              }
          },
          "HARRIS TEETER  FM": {
              "31/40": {
                  "12/12 OZ": "BPFG-CPTONP-002",
                  "12 OZ": "BPSFG-CPTONP-002"
              },
              "51/60": {
                  "12/12 OZ": "BPFG-CPTONP-003",
                  "12 OZ": "BPSFG-CPTONP-003"
              }
          },
          "SISTER SIZE": {
              "31/40": {
                  "1/10 KG": "BPSFG-CPTONP-PBBC-01",
                  "10 KG": "BPSFG-CPTONP-PBBG-01"
              }
          },
          "PLAIN BAG BULK": {
              "51/60": {
                  "1/10 KG": "BPSFG-CPTONP-PBBC-02",
                  "10 KG": "BPSFG-CPTONP-PBBG-02"
              },
              "41/50": {
                  "1/10 KG": "BPSFG-CPTONP-PBBC-03",
                  "10 KG": "BPSFG-CPTONP-PBBG-03"
              },
              "31/40": {
                  "1/10 KG": "BPSFG-CPTONP-PBBC-1A",
                  "10 KG": "BPSFG-CPTONP-PBBG-1A"
              }
          }
      },
      "REZP": {
          "BOWL AND BASKET": {
              "31/40": {
                  "10/2 LB": "BPFG-REZP-001",
                  "2 LB": "BPSFG-REZP-BG-001"
              },
              "51/60": {
                  "10/2 LB": "BPFG-REZP-002",
                  "20/1 LB": "BPFG-REZP-005",
                  "2 LB": "BPSFG-REZP-BG-002",
                  "1 LB": "BPSFG-REZP-BG-005"
              },
              "21/25": {
                  "10/2 LB": "BPFG-REZP-004",
                  "2 LB": "BPSFG-REZP-BG-004"
              },
              "16/20": {
                  "10/2 LB": "BPFG-REZP-017",
                  "2 LB": "BPSFG-REZP-017"
              }
          },
          "GREAT CATCH": {
              "31/40": {
                  "10/2 LB": "BPFG-REZP-003",
                  "2 LB": "BPSFG-REZP-BG-003"
              },
              "41/60": {
                  "24/12 OZ": "BPFG-REZP-010",
                  "12 OZ": "BPSFG-REZP-010"
              },
              "16/25": {
                  "10/2 LB": "BPFG-REZP-011",
                  "2 LB": "BPSFG-REZP-011"
              },
              "13/15": {
                  "10/32 OZ": "BPFG-REZP-012",
                  "32 OZ": "BPSFG-REZP-012"
              }
          },
          "GOLD STAR": {
              "21/25": {
                  "10/2 LB": "BPFG-REZP-006",
                  "2 LB": "BPSFG-REZP-BG-006"
              },
              "26/30": {
                  "10/2 LB": "BPFG-REZP-007",
                  "2 LB": "BPSFG-REZP-BG-007"
              },
              "31/40": {
                  "10/2 LB": "BPFG-REZP-008",
                  "20/1 LB": "BPFG-REZP-013",
                  "1 LB": "BPSFG-REZP-013",
                  "2 LB": "BPSFG-REZP-BG-008"
              },
              "16/20": {
                  "10/2 LB": "BPFG-REZP-009",
                  "2 LB": "BPSFG-REZP-009"
              }
          },
          "MARKET BASKET": {
              "26/30": {
                  "10/2 LB": "BPFG-REZP-014",
                  "20/1 LB": "BPFG-REZP-015",
                  "2 LB": "BPSFG-REZP-014",
                  "1 LB": "BPSFG-REZP-015"
              }
          },
          "SOUTHERN SEAS": {
              "26/30": {
                  "5/2 LB": "BPFG-REZP-016",
                  "2 LB": "BPSFG-REZP-016"
              }
          },
          "SAMPLES": {
              "16/20": {
                  "4 LB": "BPSFG-REZP-018"
              },
              "26/30": {
                  "4 LB": "BPSFG-REZP-019"
              },
              "31/40": {
                  "4 LB": "BPSFG-REZP-020"
              }
          },
          "FINAL BAG BULK": {
              "21/25": {
                  "10/2 LB": "BPSFG-REZP-FBBC-008",
                  "8/2 LB": "BPSFG-REZP-FBBC-003",
                  "2 LB": "BPSFG-REZP-FBBG-008"
              },
              "31/40": {
                  "10/2 LB": "BPSFG-REZP-FBBC-06A",
                  "3/2 LBS": "BPSFG-REZP-FBBC-006",
                  "2 LB": "BPSFG-REZP-FBBG-02A"
              },
              "51/60": {
                  "15/1 LB": "BPSFG-REZP-FBBC-004",
                  "20/1 LB": "BPSFG-REZP-FBBC-005",
                  "18/1 LB": "BPSFG-REZP-FBBC-007",
                  "1 LB": "BPSFG-REZP-FBBG-007"
              },
              "26/30": {
                  "10/2 LB": "BPSFG-REZP-FBBC-012",
                  "2 LB": "BPSFG-REZP-FBBG-012"
              },
              "41/60": {
                  "24/12 OZ": "BPSFG-REZP-FBBC-010",
                  "20/12 OZ": "BPSFG-REZP-FBBC-010A",
                  "12 OZ": "BPSFG-REZP-FBBG-010"
              },
              "16/20": {
                  "10/2 LB": "BPSFG-REZP-FBBC-013",
                  "2 LB": "BPSFG-REZP-FBBG-013"
              }
          },
          "PLAIN BAG BULK": {
              "51/60": {
                  "12/1 LB": "BPSFG-REZP-PBBBC-002",
                  "1 LB": "BPSFG-REZP-PBBBG-002",
                  "10/2 LB": "BPSFG-REZP-PBBC-039",
                  "1/9 KG": "BPSFG-REZP-PBBC-045",
                  "1/10 KG": "BPSFG-REZP-PBBC-047",
                  "1/3 KG": "BPSFG-REZP-PBBC-050",
                  "2 LB": "BPSFG-REZP-PBBG-039",
                  "4 KG": "BPSFG-REZP-PBBG-044",
                  "9 KG": "BPSFG-REZP-PBBG-045",
                  "10 KG": "BPSFG-REZP-PBBG-047",
                  "3 KG": "BPSFG-REZP-PBBG-050"
              },
              "41/50": {
                  "1/5 KG": "BPSFG-REZP-PBBBC-003",
                  "5 KG": "BPSFG-REZP-PBBBG-003",
                  "12/1 LB": "BPSFG-REZP-PBBC-001",
                  "1/3 KG": "BPSFG-REZP-PBBC-003",
                  "1/7 KG": "BPSFG-REZP-PBBC-006",
                  "1/4 KG": "BPSFG-REZP-PBBC-011",
                  "1/9 KG": "BPSFG-REZP-PBBC-012",
                  "7/2 LB": "BPSFG-REZP-PBBC-017",
                  "15/2 LB": "BPSFG-REZP-PBBC-020",
                  "12/2 LB": "BPSFG-REZP-PBBC-021",
                  "8/2 LB": "BPSFG-REZP-PBBC-024",
                  "14/2 LB": "BPSFG-REZP-PBBC-026",
                  "10/2 LB": "BPSFG-REZP-PBBC-028",
                  "5/2 LB": "BPSFG-REZP-PBBC-029",
                  "6/2 LB": "BPSFG-REZP-PBBC-031",
                  "1 LB": "BPSFG-REZP-PBBG-001",
                  "3 KG": "BPSFG-REZP-PBBG-003",
                  "7 KG": "BPSFG-REZP-PBBG-006",
                  "4 KG": "BPSFG-REZP-PBBG-011",
                  "9 KG": "BPSFG-REZP-PBBG-012",
                  "2 LB": "BPSFG-REZP-PBBG-017",
                  "1 KG": "BPSFG-REZP-PBBG-025"
              },
              "31/40": {
                  "1/9 KG": "BPSFG-REZP-PBBC-004",
                  "7/2 LB": "BPSFG-REZP-PBBC-014",
                  "3/9 KG": "BPSFG-REZP-PBBC-032",
                  "1/16 KG": "BPSFG-REZP-PBBC-033",
                  "1/10 KG": "BPSFG-REZP-PBBC-034",
                  "1/4 KG": "BPSFG-REZP-PBBC-035",
                  "10/2 LB": "BPSFG-REZP-PBBC-036",
                  "16/2 LB": "BPSFG-REZP-PBBC-037",
                  "1/11 KG": "BPSFG-REZP-PBBC-038",
                  "1/6 KG": "BPSFG-REZP-PBBC-041",
                  "5/2 LB": "BPSFG-REZP-PBBC-044",
                  "1/7 KG": "BPSFG-REZP-PBBC-046",
                  "9 KG": "BPSFG-REZP-PBBG-004",
                  "2 LB": "BPSFG-REZP-PBBG-014",
                  "16 KG": "BPSFG-REZP-PBBG-033",
                  "10 KG": "BPSFG-REZP-PBBG-034",
                  "4 KG": "BPSFG-REZP-PBBG-035",
                  "11 KG": "BPSFG-REZP-PBBG-038",
                  "6 KG": "BPSFG-REZP-PBBG-041",
                  "7 KG": "BPSFG-REZP-PBBG-046",
                  "12 OZ": "BPSFG-REZP-PBBG-062"
              },
              "21/25": {
                  "1/9 KG": "BPSFG-REZP-PBBC-005",
                  "1/10 KG": "BPSFG-REZP-PBBC-007",
                  "12/1 LB": "BPSFG-REZP-PBBC-008",
                  "12/2 LB": "BPSFG-REZP-PBBC-009",
                  "7/2 LB": "BPSFG-REZP-PBBC-010",
                  "10/2 LB": "BPSFG-REZP-PBBC-018",
                  "5/2 LB": "BPSFG-REZP-PBBC-030",
                  "9 KG": "BPSFG-REZP-PBBG-005",
                  "10 KG": "BPSFG-REZP-PBBG-007",
                  "1 LB": "BPSFG-REZP-PBBG-008",
                  "2 LB": "BPSFG-REZP-PBBG-030"
              },
              "26/30": {
                  "7/2 LB": "BPSFG-REZP-PBBC-013",
                  "1/6 KG": "BPSFG-REZP-PBBC-027",
                  "10/2 LB": "BPSFG-REZP-PBBC-040",
                  "1/9 KG": "BPSFG-REZP-PBBC-042",
                  "1/10 KG": "BPSFG-REZP-PBBC-051",
                  "2 LB": "BPSFG-REZP-PBBG-013",
                  "6 KG": "BPSFG-REZP-PBBG-027",
                  "9 KG": "BPSFG-REZP-PBBG-042",
                  "10 KG": "BPSFG-REZP-PBBG-051"
              },
              "ALL MIX": {
                  "1/11.85 KG": "BPSFG-REZP-PBBC-019",
                  "1/9.6 KG": "BPSFG-REZP-PBBC-022",
                  "1/10 KG": "BPSFG-REZP-PBBC-023",
                  "3 KG": "BPSFG-REZP-PBBG-015",
                  "11.85 KG": "BPSFG-REZP-PBBG-019",
                  "9.6 KG": "BPSFG-REZP-PBBG-022",
                  "10 KG": "BPSFG-REZP-PBBG-023"
              },
              "16/20": {
                  "1/10 KG": "BPSFG-REZP-PBBC-054",
                  "10 KG": "BPSFG-REZP-PBBG-054"
              },
              "13/15": {
                  "1/10 KG": "BPSFG-REZP-PBBC-056",
                  "10 KG": "BPSFG-REZP-PBBG-056"
              },
              "41/60": {
                  "1/10 KG": "BPSFG-REZP-PBBC-057",
                  "10 KG": "BPSFG-REZP-PBBG-057"
              },
              "16/25": {
                  "1/10 KG": "BPSFG-REZP-PBBC-059",
                  "10 KG": "BPSFG-REZP-PBBG-059"
              },
              "2026-12-08 00:00:00": {
                  "1/10 KG": "BPSFG-REZP-PBBC-061",
                  "10 KG": "BPSFG-REZP-PBBG-061"
              }
          },
          "REJECTION": {
              "ALL MIX": {
                  "1 KG": "BPSFG-REZP-PBBG-043",
                  "1/10 KG": "BPSFG-REZP-PBBC-043A",
                  "10 KG": "BPSFG-REZP-PBBG-043A"
              }
          },
          "QC SAMPLE": {
              "21/25": {
                  "1/1 KG": "BPSFG-REZP-PBBC-048",
                  "1 KG": "BPSFG-REZP-PBBG-048"
              }
          },
          "SISTER SIZE": {
              "21/25": {
                  "1/10 KG": "BPSFG-REZP-PBBC-049",
                  "10 KG": "BPSFG-REZP-PBBG-049"
              },
              "26/30": {
                  "1/10 KG": "BPSFG-REZP-PBBC-052",
                  "10 KG": "BPSFG-REZP-PBBG-052"
              },
              "16/20": {
                  "1/10 KG": "BPSFG-REZP-PBBC-053",
                  "10 KG": "BPSFG-REZP-PBBG-053"
              },
              "31/40": {
                  "1/10 KG": "BPSFG-REZP-PBBC-055",
                  "10 KG": "BPSFG-REZP-PBBG-055"
              },
              "13/15": {
                  "1/10 KG": "BPSFG-REZP-PBBC-056A",
                  "10 KG": "BPSFG-REZP-PBBG-056A"
              },
              "41/60": {
                  "1/10 KG": "BPSFG-REZP-PBBC-058",
                  "10 KG": "BPSFG-REZP-PBBG-058"
              },
              "51/60": {
                  "1/10 KG": "BPSFG-REZP-PBBC-060",
                  "10 KG": "BPSFG-REZP-PBBG-060"
              }
          },
          "DE COLOUR": {
              "DE COLOR": {
                  "7 KG": "BPSFG-REZP-PBBG-016"
              }
          }
      },
      "REZPNC": {
          "FREMONT FISH MARKET": {
              "41/60": {
                  "16/12 OZ": "BPFG-REZPNC-001",
                  "12 OZ": "BPSFG-REZPNC-001"
              }
          },
          "SAMPLES": {
              "16/20": {
                  "4 LB": "BPSFG-REZPNC-002"
              },
              "26/30": {
                  "4 LB": "BPSFG-REZPNC-003"
              },
              "31/40": {
                  "4 LB": "BPSFG-REZPNC-004"
              }
          },
          "FINAL BAG BULK": {
              "41/60": {
                  "20/12 OZ": "BPSFG-REZPNC-FBBC-01",
                  "24/12 OZ": "BPSFG-REZPNC-FBBC-1A",
                  "12 OZ": "BPSFG-REZPNC-FBBG-01"
              }
          },
          "PLAIN BAG BULK": {
              "41/60": {
                  "1/10 KG": "BPSFG-REZPNC-PBBC-01",
                  "10 KG": "BPSFG-REZPNC-PBBG-01"
              }
          },
          "SISTER SIZE": {
              "41/60": {
                  "1/10 KG": "BPSFG-REZPNC-PBBC-1A",
                  "10 KG": "BPSFG-REZPNC-PBBG-1A"
              }
          }
      },
      "REZPNC-TG": {
          "CHOICE HARVEST": {
              "2026-12-08 00:00:00": {
                  "10/1 LB": "BPFG-REZPNCTG-001",
                  "1 LB": "BPSFG-REZPNCTG-001"
              }
          },
          "TC HEB": {
              "2026-08-06 00:00:00": {
                  "5/2 LB": "BPFG-REZPNCTG-002",
                  "2 LB": "BPSFG-REZPTGNC-002"
              },
              "16/20": {
                  "5/2 LB": "BPFG-REZPTGNC-001",
                  "2 LB": "BPSFG-REZPTG-001"
              }
          },
          "PLAIN BAG BULK": {
              "41/50": {
                  "1/10 KG": "BPFG-REZPNCTG-PBC-01",
                  "10 KG": "BPFG-REZPNCTG-PBG-01"
              },
              "26/30": {
                  "1/10 KG": "BPFG-REZPNCTG-PBC-02",
                  "10 KG": "BPFG-REZPNCTG-PBG-02"
              },
              "13/15": {
                  "1/10 KG": "BPFG-REZPTG-PBBC-001",
                  "10 KG": "BPFG-REZPTG-PBBG-001"
              },
              "2026-08-06 00:00:00": {
                  "1/10 KG": "BPFG-REZPTG-PBBC-003",
                  "10 KG": "BPFG-REZPTG-PBBG-003"
              },
              "2026-12-08 00:00:00": {
                  "1/10 KG": "BPSFG-REZPTG-PBBC-02",
                  "15/1 LB": "BPSFG-REZPTG-PBBC-03",
                  "10 KG": "BPSFG-REZPTG-PBBG-02",
                  "1 LB": "BPSFG-REZPTG-PBBG-03"
              }
          },
          "FINAL BAG BULK": {
              "2026-12-08 00:00:00": {
                  "15/1 LB": "BPSFG-REZPTG-FBBC-01",
                  "10/1 LB": "BPSFG-REZPTG-FBBC01A",
                  "1 LB": "BPSFG-REZPTG-FBBG-01"
              },
              "2026-08-06 00:00:00": {
                  "8/2 LB": "BPSFG-REZPTG-FBBC-03",
                  "10/2 LB": "BPSFG-REZPTG-FBBC-3A",
                  "2 LB": "BPSFG-REZPTG-FBBG-03"
              }
          }
      },
      "RPND": {
          "TC": {
              "71/90": {
                  "1/22 LB": "BPFG-RPND-001",
                  "22 LB": "BPSFG-RPND-001"
              }
          },
          "SOUTHERN SEAS": {
              "26/30": {
                  "5/2 LB": "BPFG-RPND-002",
                  "2 LB": "BPSFG-RPND-002"
              }
          },
          "FINAL BAG BULK": {
              "71/90": {
                  "1/22 LB": "BPSFG-RPND-FBBC-001",
                  "22 LB": "BPSFG-RPND-FBBG-001"
              }
          },
          "PLAIN BAG BULK": {
              "71/90": {
                  "12/1 LB": "BPSFG-RPND-PBBC-001",
                  "1/2.8 KG": "BPSFG-RPND-PBBC-004",
                  "1/9 KG": "BPSFG-RPND-PBBC-007",
                  "1/3.5 KG": "BPSFG-RPND-PBBC-008",
                  "1/1.6 KG": "BPSFG-RPND-PBBC-011",
                  "1/2.7 KG": "BPSFG-RPND-PBBC-012",
                  "1/10.5 KG": "BPSFG-RPND-PBBC-014",
                  "1/5.3 KG": "BPSFG-RPND-PBBC-019",
                  "1/10 KG": "BPSFG-RPND-PBBC-021",
                  "1 LB": "BPSFG-RPND-PBBG-001",
                  "2.8 KG": "BPSFG-RPND-PBBG-004",
                  "9 KG": "BPSFG-RPND-PBBG-007",
                  "3.5 KG": "BPSFG-RPND-PBBG-008",
                  "1.6 KG": "BPSFG-RPND-PBBG-011",
                  "2.7 KG": "BPSFG-RPND-PBBG-012",
                  "10.5 KG": "BPSFG-RPND-PBBG-014",
                  "5.3 KG": "BPSFG-RPND-PBBG-019",
                  "10 KG": "BPSFG-RPND-PBBG-021",
                  "2 LB": "BPSFG-RPND-PBBG-030"
              },
              "91/110": {
                  "10/1 LB": "BPSFG-RPND-PBBC-002",
                  "1/3.2 KG": "BPSFG-RPND-PBBC-009",
                  "1/5 KG": "BPSFG-RPND-PBBC-010",
                  "1/6.2 KG": "BPSFG-RPND-PBBC-015",
                  "1/9 KG": "BPSFG-RPND-PBBC-016",
                  "1/4 KG": "BPSFG-RPND-PBBC-017",
                  "1/3 KG": "BPSFG-RPND-PBBC-020",
                  "1/10 KG": "BPSFG-RPND-PBBC-022",
                  "1 LB": "BPSFG-RPND-PBBG-002",
                  "3.2 KG": "BPSFG-RPND-PBBG-009",
                  "5 KG": "BPSFG-RPND-PBBG-010",
                  "6.2 KG": "BPSFG-RPND-PBBG-015",
                  "9 KG": "BPSFG-RPND-PBBG-016",
                  "4 KG": "BPSFG-RPND-PBBG-017",
                  "3 KG": "BPSFG-RPND-PBBG-020",
                  "10 KG": "BPSFG-RPND-PBBG-022"
              },
              "61/70": {
                  "1/3 KG": "BPSFG-RPND-PBBC-003",
                  "1/6 KG": "BPSFG-RPND-PBBC-005",
                  "10/1 LB": "BPSFG-RPND-PBBC-006",
                  "3 KG": "BPSFG-RPND-PBBG-003",
                  "6 KG": "BPSFG-RPND-PBBG-005",
                  "1 LB": "BPSFG-RPND-PBBG-006"
              },
              "100/150": {
                  "1/1.3 KG": "BPSFG-RPND-PBBC-013",
                  "1/10 KG": "BPSFG-RPND-PBBC-034",
                  "1.3 KG": "BPSFG-RPND-PBBG-013",
                  "10 KG": "BPSFG-RPND-PBBG-034"
              },
              "ALL MIX": {
                  "1 KG": "BPSFG-RPND-PBBG-018"
              },
              "31/40": {
                  "1/10 KG": "BPSFG-RPND-PBBC-023",
                  "10 KG": "BPSFG-RPND-PBBG-023"
              },
              "41/50": {
                  "1/10 KG": "BPSFG-RPND-PBBC-024",
                  "10 KG": "BPSFG-RPND-PBBG-024"
              },
              "26/30": {
                  "1/10 KG": "BPSFG-RPND-PBBC-025",
                  "10 KG": "BPSFG-RPND-PBBG-025"
              },
              "20/40": {
                  "1/10 KG": "BPSFG-RPND-PBBC-027A",
                  "10 KG": "BPSFG-RPND-PBBG-027A",
                  "1 KG": "BPSFG-RPND-PBBG-027B"
              },
              "60/80": {
                  "1/10 KG": "BPSFG-RPND-PBBC-028",
                  "10 KG": "BPSFG-RPND-PBBG-028"
              },
              "40/60": {
                  "1/10 KG": "BPSFG-RPND-PBBC-029",
                  "10 KG": "BPSFG-RPND-PBBG-029"
              },
              "100/200": {
                  "1/10 KG": "BPSFG-RPND-PBBC-035",
                  "10 KG": "BPSFG-RPND-PBBG-035"
              }
          },
          "DE COLOUR": {
              "71/90": {
                  "1/10 KG": "BPSFG-RPND-PBBC-026",
                  "10 KG": "BPSFG-RPND-PBBG-026"
              },
              "13/15": {
                  "1/10 KG": "BPSFG-RPND-PBBC-032",
                  "10 KG": "BPSFG-RPND-PBBG-032"
              }
          }
      },
      "RPND-NP": {
          "HARRIS TEETER  FM": {
              "51/60": {
                  "12/12 OZ": "BPFG-RPNDNP-001",
                  "12 OZ": "BPSFG-RPNDNP-001"
              }
          },
          "FINAL BAG BULK": {
              "51/60": {
                  "20/12 OZ": "BPFG-RPNDNP-FBBC-001",
                  "12 OZ": "BPFG-RPNDNP-FBBG-001"
              }
          },
          "PLAIN BAG BULK": {
              "51/60": {
                  "1/10 KG": "BPFG-RPNDNP-PBBC-001",
                  "10 KG": "BPFG-RPNDNP-PBBG-001"
              }
          }
      },
      "RPTO": {
          "BOWL AND BASKET": {
              "21/25": {
                  "10/2 LB": "BPFG-RPTO-001",
                  "2 LB": "BPSFG-RPTO-001"
              },
              "31/40": {
                  "20/1 LB": "BPFG-RPTO-002",
                  "10/2 LB": "BPFG-RPTO-100",
                  "1 LB": "BPSFG-RPTO-002",
                  "2 LB": "BPSFG-RPTO-BG-120"
              }
          },
          "TC BLOOMIN": {
              "26/30": {
                  "6/5 LB": "BPFG-RPTO-003",
                  "5 LB": "BPSFG-RPTO-003"
              },
              "16/20": {
                  "6/5 LB": "BPFG-RPTO-004",
                  "5 LB": "BPSFG-RPTO-004"
              }
          },
          "FINAL BAG BULK": {
              "31/40": {
                  "10/2 LB": "BPSFG-RPTO-FBBC-001",
                  "8/2 LB": "BPSFG-RPTO-FBBC-003",
                  "2 LB": "BPSFG-RPTO-FBBG-001"
              },
              "21/25": {
                  "10/2 LB": "BPSFG-RPTO-FBBC-002",
                  "2 LB": "BPSFG-RPTO-FBBG-002"
              },
              "26/30": {
                  "4/5 LB": "BPSFG-RPTO-FBBC-004",
                  "5 LB": "BPSFG-RPTO-FBBG-004"
              },
              "16/20": {
                  "6/5 LB": "BPSFG-RPTO-FBBC-005",
                  "5 LB": "BPSFG-RPTO-FBBG-005"
              }
          },
          "PLAIN BAG BULK": {
              "31/40": {
                  "1/5 KG": "BPSFG-RPTO-PBBC-001",
                  "1/6 KG": "BPSFG-RPTO-PBBC-002",
                  "1/9 KG": "BPSFG-RPTO-PBBC-003",
                  "1/10 KG": "BPSFG-RPTO-PBBC-004",
                  "1/7 KG": "BPSFG-RPTO-PBBC-007",
                  "5 KG": "BPSFG-RPTO-PBBG-001",
                  "6 KG": "BPSFG-RPTO-PBBG-002",
                  "9 KG": "BPSFG-RPTO-PBBG-003",
                  "10 KG": "BPSFG-RPTO-PBBG-004",
                  "7 KG": "BPSFG-RPTO-PBBG-007"
              },
              "21/25": {
                  "10/2 LB": "BPSFG-RPTO-PBBC-005",
                  "1/9 KG": "BPSFG-RPTO-PBBC-006",
                  "1/10 KG": "BPSFG-RPTO-PBBC-008",
                  "2 LB": "BPSFG-RPTO-PBBG-005",
                  "9 KG": "BPSFG-RPTO-PBBG-006",
                  "10 KG": "BPSFG-RPTO-PBBG-008",
                  "16 OZ": "BPSFG-RPTO-PBBG-016",
                  "12 OZ": "BPSFG-RPTO-PBBG-017"
              },
              "26/30": {
                  "1/10 KG": "BPSFG-RPTO-PBBC-009",
                  "10 KG": "BPSFG-RPTO-PBBG-009"
              },
              "16/20": {
                  "1/10 KG": "BPSFG-RPTO-PBBC-012",
                  "10 KG": "BPSFG-RPTO-PBBG-012",
                  "16 OZ": "BPSFG-RPTO-PBBG-015"
              },
              "MIX": {
                  "1/10 KG": "BPSFG-RPTO-PBBC-013",
                  "10 KG": "BPSFG-RPTO-PBBG-013"
              },
              "41/50": {
                  "12 OZ": "BPSFG-RPTO-PBBG-018"
              }
          },
          "SISTER SIZE": {
              "21/25": {
                  "1/10 KG": "BPSFG-RPTO-PBBC-010",
                  "10 KG": "BPSFG-RPTO-PBBG-010"
              },
              "31/40": {
                  "1/10 KG": "BPSFG-RPTO-PBBC-011",
                  "10 KG": "BPSFG-RPTO-PBBG-011"
              },
              "16/20": {
                  "1/10 KG": "BPSFG-RPTO-PBBC-012A",
                  "10 KG": "BPSFG-RPTO-PBBG-012A"
              }
          },
          "REJECTION": {
              "MIX": {
                  "1/10 KG": "BPSFG-RPTO-PBBC-014",
                  "10 KG": "BPSFG-RPTO-PBBG-014"
              }
          }
      },
      "RTNC": {
          "WHOLESOME PANTRY": {
              "16/20": {
                  "5/2 LB": "BPFG-RTNC-001",
                  "20/1 LB": "BPFG-RTNC-004",
                  "2 LB": "BPSFG-RTNC-001",
                  "1 LB": "BPSFG-RTNC-004"
              },
              "21/25": {
                  "5/2 LB": "BPFG-RTNC-009",
                  "20/1 LB": "BPFG-RTNC-010",
                  "2 LB": "BPSFG-RTNC-BG-009",
                  "1 LB": "BPSFG-RTNC-BG-010"
              },
              "31/40": {
                  "5/2 LB": "BPFG-RTNC-019",
                  "20/1 LB": "BPFG-RTNC-020",
                  "10/2 LB": "BPFG-RTNC-055",
                  "2 LB": "BPSFG-RTNC-BG-017",
                  "1 LB": "BPSFG-RTNC-BG-018"
              }
          },
          "NATURES PROMISE": {
              "21/25": {
                  "12/12 OZ": "BPFG-RTNC-002",
                  "12 OZ": "BPSFG-RTNC-002"
              }
          },
          "FREMONT FISH MARKET": {
              "31/40": {
                  "10/2 LB": "BPFG-RTNC-003",
                  "2 LB": "BPSFG-RTNC-003"
              }
          },
          "FINAL BAG BULK": {
              "21/25": {
                  "6/2 LB": "BPSFG-RTNC-FBBC-006",
                  "12/2 LB": "BPSFG-RTNC-FBBC-007",
                  "20/1 LB": "BPSFG-RTNC-FBBC-010",
                  "15/1 LB": "BPSFG-RTNC-FBBC-10A",
                  "2 LB": "BPSFG-RTNC-FBBG-007",
                  "1 LB": "BPSFG-RTNC-FBBG-010"
              },
              "31/40": {
                  "20/1 LB": "BPSFG-RTNC-FBBC-008",
                  "15/1 LB": "BPSFG-RTNC-FBBC-008A",
                  "10/2 LB": "BPSFG-RTNC-FBBC-009",
                  "1 LB": "BPSFG-RTNC-FBBG-008A",
                  "2 LB": "BPSFG-RTNC-FBBG-009"
              },
              "16/20": {
                  "15/1 LB": "BPSFG-RTNC-FBBC-011",
                  "1 LB": "BPSFG-RTNC-FBBG-011"
              }
          },
          "PLAIN BAG BULK": {
              "21/25": {
                  "1/5 KG": "BPSFG-RTNC-PBBC-001",
                  "7/2 LB": "BPSFG-RTNC-PBBC-002",
                  "10/1 LB": "BPSFG-RTNC-PBBC-003",
                  "16/1 LB": "BPSFG-RTNC-PBBC-003A",
                  "1/9 KG": "BPSFG-RTNC-PBBC-004",
                  "8/2 LB": "BPSFG-RTNC-PBBC-005",
                  "15/2 LB": "BPSFG-RTNC-PBBC-006",
                  "14/2 LB": "BPSFG-RTNC-PBBC-008",
                  "2/2 LB": "BPSFG-RTNC-PBBC-009",
                  "10/2 LB": "BPSFG-RTNC-PBBC-010",
                  "1/10 KG": "BPSFG-RTNC-PBBC-021",
                  "5 KG": "BPSFG-RTNC-PBBG-001",
                  "2 LB": "BPSFG-RTNC-PBBG-002",
                  "1 LB": "BPSFG-RTNC-PBBG-003",
                  "9 KG": "BPSFG-RTNC-PBBG-004",
                  "10 KG": "BPSFG-RTNC-PBBG-021"
              },
              "31/40": {
                  "8/2 LB": "BPSFG-RTNC-PBBC-011",
                  "10/2 LB": "BPSFG-RTNC-PBBC-011A",
                  "1/5 KG": "BPSFG-RTNC-PBBC-012",
                  "1/9 KG": "BPSFG-RTNC-PBBC-014",
                  "1/4 KG": "BPSFG-RTNC-PBBC-015",
                  "1/6 KG": "BPSFG-RTNC-PBBC-016",
                  "1/16 KG": "BPSFG-RTNC-PBBC-017",
                  "1/10 KG": "BPSFG-RTNC-PBBC-022",
                  "2 LB": "BPSFG-RTNC-PBBG-011",
                  "5 KG": "BPSFG-RTNC-PBBG-012",
                  "9 KG": "BPSFG-RTNC-PBBG-014",
                  "4 KG": "BPSFG-RTNC-PBBG-015",
                  "6 KG": "BPSFG-RTNC-PBBG-016",
                  "16 KG": "BPSFG-RTNC-PBBG-017",
                  "10 KG": "BPSFG-RTNC-PBBG-022"
              },
              "16/20": {
                  "14/2 LB": "BPSFG-RTNC-PBBC-013",
                  "1/10 KG": "BPSFG-RTNC-PBBC-018A",
                  "10/2 LB": "BPSFG-RTNC-PBBC-019",
                  "2 LB": "BPSFG-RTNC-PBBG-013",
                  "10 KG": "BPSFG-RTNC-PBBG-018A"
              }
          },
          "SISTER SIZE": {
              "16/20": {
                  "1/10 KG": "BPSFG-RTNC-PBBC-018",
                  "10 KG": "BPSFG-RTNC-PBBG-018"
              },
              "21/25": {
                  "1/10 KG": "BPSFG-RTNC-PBBC-020",
                  "10 KG": "BPSFG-RTNC-PBBG-020"
              },
              "31/40": {
                  "1/10 KG": "BPSFG-RTNC-PBBC-022A",
                  "10 KG": "BPSFG-RTNC-PBBG-022A"
              }
          }
      },
      "RTNC-TG": {
          "BOWL AND BASKET": {
              "13/15": {
                  "10/2 LB": "BPFG-RTNCTG-001",
                  "2 LB": "BPSFG-RTNCTG-001"
              }
          },
          "FINAL BAG BULK": {
              "13/15": {
                  "10/2 LB": "BPSFG-RTNCTG-FBBC-01",
                  "9/2 LB": "BPSFG-RTNCTG-FBBC-02",
                  "2 LB": "BPSFG-RTNCTG-FBBG-02"
              }
          },
          "PLAIN BAG BULK": {
              "13/15": {
                  "1/10 KG": "BPSFG-RTNCTG-PBBC-02",
                  "10 KG": "BPSFG-RTNCTG-PBBG-02"
              }
          }
      },
      "CBKN": {
          "PLAIN BAG BULK": {
              "BROKEN": {
                  "1/10 KG": "BPSFG-CBKN-PBBC-002",
                  "1 KG": "BPSFG-CBKN-PBBG-001",
                  "10 KG": "BPSFG-CBKN-PBBG-002"
              },
              "FALLEN PIE": {
                  "1/10 KG": "BPSFG-CBKN-PBBC-003",
                  "10 KG": "BPSFG-CBKN-PBBG-003"
              },
              "20/40": {
                  "1/10 KG": "BPSFG-CBKN-PBBC-004",
                  "10 KG": "BPSFG-CBKN-PBBG-004"
              },
              "40/60": {
                  "1/10 KG": "BPSFG-CBKN-PBBC-005",
                  "10 KG": "BPSFG-CBKN-PBBG-005"
              },
              "60/80": {
                  "1/10 KG": "BPSFG-CBKN-PBBC-006",
                  "10 KG": "BPSFG-CBKN-PBBG-006"
              }
          }
      },
      "CPND-BKN": {
          "PLAIN BAG BULK": {
              "20/40": {
                  "1/10 KG": "BPSFG-CPND-PBBC-013",
                  "10 KG": "BPSFG-CPND-PBBG-013"
              },
              "40/60": {
                  "1/10 KG": "BPSFG-CPND-PBBC-014",
                  "10 KG": "BPSFG-CPND-PBBG-014"
              },
              "ALL MIX": {
                  "1/10 KG": "BPSFG-CPND-PBBC-015",
                  "10 KG": "BPSFG-CPND-PBBG-015"
              },
              "60/80": {
                  "1/10 KG": "BPSFG-CPND-PBBC-017",
                  "10 KG": "BPSFG-CPND-PBBG-017"
              }
          }
      },
      "CPTO-NC": {
          "SAMPLES": {
              "16/20": {
                  "4 LB": "BPSFG-CPTONC-BG-001"
              },
              "26/30": {
                  "4 LB": "BPSFG-CPTONC-BG-002"
              },
              "31/40": {
                  "4 LB": "BPSFG-CPTONC-BG-003"
              }
          }
      },
      "CISTO": {
          "PLAIN BAG BULK": {
              "50/70": {
                  "1/10 KG": "BPSFG-CSIST0-PBBC-01",
                  "10 KG": "BPSFG-CSIST0-PBBG-01"
              }
          }
      },
      "HLSO-BT": {
          "PLAIN BAG BULK": {
              "26/30": {
                  "1/10 KG": "BPSFG-HLSOBT-PBBC-01",
                  "10 KG": "BPSFG-HLSOBT-PBBG-01"
              },
              "31/40": {
                  "1/10 KG": "BPSFG-HLSOBT-PBBC-05",
                  "10 KG": "BPSFG-HLSOBT-PBBG-05"
              },
              "21/25": {
                  "1/10 KG": "BPSFG-HLSOBT-PBBC-06",
                  "10 KG": "BPSFG-HLSOBT-PBBG-06"
              },
              "16/20": {
                  "1/10 KG": "BPSFG-HLSOBT-PBBC-07",
                  "10 KG": "BPSFG-HLSOBT-PBBG-07"
              },
              "ALL MIX": {
                  "1/10 KG": "BPSFG-HLSOBT-PBBC-08",
                  "10 KG": "BPSFG-HLSOBT-PBBG-08"
              },
              "41/50": {
                  "1/10 KG": "BPSFG-HLSOBT-PBBC-09",
                  "10 KG": "BPSFG-HLSOBT-PBBG-09"
              },
              "13/15": {
                  "1/10 KG": "BPSFG-HLSOBT-PBBC-10",
                  "10 KG": "BPSFG-HLSOBT-PBBG-10"
              },
              "2026-08-06 00:00:00": {
                  "1/10 KG": "BPSFG-HLSOBT-PBBC-4A",
                  "10 KG": "BPSFG-HLSOBT-PBBG-4A"
              }
          },
          "BLACK TAIL": {
              "21/25": {
                  "1/10 KG": "BPSFG-HLSOBT-PBBC-02",
                  "10 KG": "BPSFG-HLSOBT-PBBG-02"
              },
              "31/40": {
                  "1/10 KG": "BPSFG-HLSOBT-PBBC-03",
                  "10 KG": "BPSFG-HLSOBT-PBBG-03"
              },
              "2026-08-06 00:00:00": {
                  "1/10 KG": "BPSFG-HLSOBT-PBBC-04",
                  "10 KG": "BPSFG-HLSOBT-PBBG-04"
              }
          }
      },
      "HLSO": {
          "PLAIN BAG BULK": {
              "60/80": {
                  "1/10 KG": "BPSFG-HLSO-PBBC-01",
                  "10 KG": "BPSFG-HLSO-PBBG-01"
              },
              "31/40": {
                  "1/10 KG": "BPSFG-HLSO-PBBC-02",
                  "10 KG": "BPSFG-HLSO-PBBG-02"
              },
              "ALL MIX": {
                  "1/10 KG": "BPSFG-HLSO-PBBC-03",
                  "10 KG": "BPSFG-HLSO-PBBG-03"
              },
              "2026-12-08 00:00:00": {
                  "1/10 KG": "BPSFG-HLSO-PBBC-04",
                  "10 KG": "BPSFG-HLSO-PBBG-04"
              },
              "100/200": {
                  "1/10 KG": "BPSFG-HLSO-PBBC-05",
                  "10 KG": "BPSFG-HLSO-PBBG-05"
              },
              "100/150": {
                  "1/10 KG": "BPSFG-HLSO-PBBC-06",
                  "10 KG": "BPSFG-HLSO-PBBG-06"
              }
          }
      },
      "RBKN": {
          "PLAIN BAG BULK": {
              "BROKEN": {
                  "1/10 KG": "BPSFG-RBKN-PBBC-002",
                  "1 KG": "BPSFG-RBKN-PBBG-001",
                  "10 KG": "BPSFG-RBKN-PBBG-002"
              },
              "FALLEN PIE": {
                  "10 KG": "BPSFG-RBKN-PBBG-003"
              },
              "20/40": {
                  "1/10 KG": "BPSFG-RBKN-PBBC-004",
                  "10 KG": "BPSFG-RBKN-PBBG-004"
              },
              "40/60": {
                  "1/10 KG": "BPSFG-RBKN-PBBC-005",
                  "10 KG": "BPSFG-RBKN-PBBG-005"
              },
              "60/80": {
                  "1/10 KG": "BPSFG-RBKN-PBBC-006",
                  "10 KG": "BPSFG-RBKN-PBBG-006"
              }
          }
      },
      "RDCS": {
          "PLAIN BAG BULK": {
              "DE-COLOUR": {
                  "1/10 KG": "BPSFG-RDCS-PBBC-001",
                  "10 KG": "BPSFG-RDCS-PBBG-001"
              }
          }
      },
      "REZP-TG": {
          "FINAL BAG BULK": {
              "2026-08-06 00:00:00": {
                  "9/2 LB": "BPSFG-REZPTG-FBBC-02",
                  "10/2 LB": "BPSFG-REZPTG-FBBC-2A",
                  "2 LB": "BPSFG-REZPTG-FBBG-02"
              }
          },
          "PLAIN BAG BULK": {
              "2026-08-06 00:00:00": {
                  "8/2 LB": "BPSFG-REZPTG-PBBC-01",
                  "10/2 LB": "BPSFG-REZPTG-PBBC-1A",
                  "2 LB": "BPSFG-REZPTG-PBBG-01"
              }
          }
      },
      "RHLW": {
          "PLAIN BAG BULK": {
              "71/90": {
                  "1/10 KG": "BPSFG-RHLW-PBBC-001",
                  "1/5 KG": "BPSFG-RHLW-PBBC-005",
                  "1/2.5 KG": "BPSFG-RHLW-PBBC-007",
                  "1/7 KG": "BPSFG-RHLW-PBBC-008",
                  "1/6 KG": "BPSFG-RHLW-PBBC-011",
                  "10 KG": "BPSFG-RHLW-PBBG-001",
                  "5 KG": "BPSFG-RHLW-PBBG-005",
                  "2.5 KG": "BPSFG-RHLW-PBBG-007",
                  "7 KG": "BPSFG-RHLW-PBBG-008",
                  "6 KG": "BPSFG-RHLW-PBBG-011"
              },
              "91/110": {
                  "1/10 KG": "BPSFG-RHLW-PBBC-002",
                  "1/1 KG": "BPSFG-RHLW-PBBC-009",
                  "10 KG": "BPSFG-RHLW-PBBG-002",
                  "1 KG": "BPSFG-RHLW-PBBG-009"
              },
              "61/70": {
                  "1/10 KG": "BPSFG-RHLW-PBBC-003",
                  "1/3 KG": "BPSFG-RHLW-PBBC-004",
                  "1/2.5 KG": "BPSFG-RHLW-PBBC-006",
                  "10 KG": "BPSFG-RHLW-PBBG-003",
                  "3 KG": "BPSFG-RHLW-PBBG-004",
                  "2.5 KG": "BPSFG-RHLW-PBBG-006"
              },
              "2026-12-08 00:00:00": {
                  "1/1 KG": "BPSFG-RHLW-PBBC-010",
                  "1 KG": "BPSFG-RHLW-PBBG-010"
              }
          }
      },
      "RPND-BKN": {
          "PLAIN BAG BULK": {
              "40/60": {
                  "1/10 KG": "BPSFG-RPND-PBBC-027",
                  "10 KG": "BPSFG-RPND-PBBG-027"
              },
              "60/80": {
                  "1/10 KG": "BPSFG-RPND-PBBC-031",
                  "10 KG": "BPSFG-RPND-PBBG-031"
              },
              "ALL MIX": {
                  "1/10 KG": "BPSFG-RPND-PBBC-033",
                  "10 KG": "BPSFG-RPND-PBBG-033"
              }
          }
      },
      "RPVPD": {
          "FINAL BAG BULK": {
              "31/40": {
                  "20/1 KG": "BPSFG-RPVPD-FBBC-001",
                  "10/1 KG": "BPSFG-RPVPD-FBBC-01A",
                  "1 KG": "BPSFG-RPVPD-FBBG-001"
              }
          }
      }
  };
};

// Export default object with all functions
export default { 
  getNavId, 
  addNewNavMapping, 
  loadNavIdsFromDatabase,
  loadDropdowns,
  getDropdownOptions,
  getAvailableProducts,
  getAvailableBrands,
  getAvailableGrades,
  getAvailablePackSizes,
  exportNavIdsToJson,
  getCacheStats
};